import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CameraIcon } from '@phosphor-icons/react';
import useSWR from 'swr';
import {
    SourcesMenu,
    type SourceSelectionOverride,
} from '@/features/SourcesMenu';
import { Button } from '@/components/ui';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { EngineService } from '@/services/engine';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { SourceType } from '@/types/source';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useOverlayManager, OverlayNames } from '@/hooks/useOverlayManager';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import {
    canCheckAlertSource,
    nextOverCapTooltip,
    remainingAlertSourceSlots,
    selectAlertSourcesUntilLimit,
    selectionExceedsAllowedAlertUnits,
    spendingSourceCount,
} from '../alert-unit-limit';

function NextButton({
    label,
    disabled,
    tooltip,
    onNext,
}: {
    label: string;
    disabled: boolean;
    tooltip: string | null;
    onNext: () => void;
}) {
    const button = (
        <Button
            onClick={onNext}
            disabled={disabled}
            className={tooltip ? 'pointer-events-none' : undefined}
        >
            {label}
        </Button>
    );
    if (!tooltip) return button;
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    tabIndex={0}
                    className="inline-flex cursor-not-allowed rounded-[5px] outline-none focus-visible:ring-2 focus-visible:ring-white-100"
                >
                    {button}
                </span>
            </TooltipTrigger>
            <TooltipContent variant="panel" className="max-w-80">
                {tooltip}
            </TooltipContent>
        </Tooltip>
    );
}

interface SourceSelectionStepProps {
    selectedSourceIds: string[];
    onSourcesChange: (update: (previous: string[]) => string[]) => void;
    onNext: () => void;
    maxAlertUnits: number | null | undefined;
    usedElsewhere: number;
    savedSourceIds: readonly string[] | null;
    error?: string;
}

export function SourceSelectionStep({
    selectedSourceIds,
    onSourcesChange,
    onNext,
    maxAlertUnits,
    usedElsewhere,
    savedSourceIds,
    error,
}: SourceSelectionStepProps) {
    const { t } = useTranslation(I18nNamespace.ALERT_RULES);
    const { open } = useOverlayManager();
    const { liveCamerasEnabled, hdmiEnabled } = useFeatureFlags();
    const { data: allCameraIds } = useSWR(
        [CACHE_KEYS.SOURCES_PREFIX, 'ids', SourceType.CAMERA],
        () => EngineService.getSourceIds({ type: SourceType.CAMERA }),
    );
    const liveSourceIds = useMemo(
        () => (allCameraIds ? new Set(allCameraIds) : null),
        [allCameraIds],
    );
    const selectedIds = useMemo(
        () => new Set(selectedSourceIds),
        [selectedSourceIds],
    );
    const selectedSpend = spendingSourceCount(selectedSourceIds, liveSourceIds);
    const selectionBlocked = selectionExceedsAllowedAlertUnits(
        usedElsewhere,
        selectedSpend,
        maxAlertUnits,
        savedSourceIds,
        selectedSourceIds,
    );
    const overCapTooltip = nextOverCapTooltip(
        usedElsewhere,
        selectedSpend,
        maxAlertUnits,
        savedSourceIds,
        selectedSourceIds,
    );
    const overCapMessage =
        overCapTooltip === null
            ? null
            : overCapTooltip.kind === 'atCap'
              ? t('form.sourceSelection.alertLimit.nextAtCap')
              : t('form.sourceSelection.alertLimit.nextOverCap', {
                    count: overCapTooltip.count,
                });
    const maxAdditionalSources = remainingAlertSourceSlots(
        maxAlertUnits,
        usedElsewhere,
        selectedSpend,
    );

    const selectMany = (ids: string[]) =>
        onSourcesChange((previous) =>
            selectAlertSourcesUntilLimit(
                usedElsewhere,
                previous,
                ids,
                maxAlertUnits,
                liveSourceIds,
                savedSourceIds,
            ),
        );

    const deselectMany = (ids: string[]) =>
        onSourcesChange((previous) =>
            previous.filter((id) => !ids.includes(id)),
        );

    const selectionOverride: SourceSelectionOverride = {
        selected: selectedIds,
        onToggle: (id) =>
            onSourcesChange((previous) => {
                if (previous.includes(id)) {
                    return previous.filter((selectedId) => selectedId !== id);
                }
                if (
                    !canCheckAlertSource(
                        id,
                        usedElsewhere,
                        new Set(previous),
                        maxAlertUnits,
                        liveSourceIds,
                        savedSourceIds,
                    )
                ) {
                    return previous;
                }
                return [...previous, id];
            }),
        onSelect: (id) => selectMany([id]),
        onDeselect: (id) => deselectMany([id]),
        onSelectMany: selectMany,
        onDeselectMany: deselectMany,
    };

    const camerasEnabled = liveCamerasEnabled || hdmiEnabled;

    if (!camerasEnabled || allCameraIds?.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[320px] gap-6">
                <CameraIcon
                    className="size-12 text-grey-400"
                    weight="regular"
                />
                <div className="flex flex-col items-center gap-2 text-center max-w-[400px]">
                    <h3 className="text-lg font-medium text-grey-200">
                        {t('form.sourceSelection.emptyState.title')}
                    </h3>
                    <p className="text-sm text-grey-400">
                        {t('form.sourceSelection.emptyState.description')}
                    </p>
                </div>
                {liveCamerasEnabled && (
                    <Button
                        onClick={() => open(OverlayNames.ADD_CAMERA, {})}
                        variant="primary"
                    >
                        {t('form.sourceSelection.emptyState.button')}
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <SourcesMenu
                variant="inline"
                includeVideos={false}
                selectionOverride={selectionOverride}
                maxAdditionalSources={maxAdditionalSources}
                isSourceDisabled={(id) =>
                    !canCheckAlertSource(
                        id,
                        usedElsewhere,
                        selectedIds,
                        maxAlertUnits,
                        liveSourceIds,
                        savedSourceIds,
                    )
                }
                disabledSourceMessage={
                    maxAlertUnits === undefined
                        ? t(
                              'errors.organization.alert_unit_count_unavailable',
                              {
                                  ns: I18nNamespace.SETTINGS,
                              },
                          )
                        : maxAlertUnits === null
                          ? undefined
                          : t('form.sourceSelection.alertLimit.atCap', {
                                max: maxAlertUnits,
                            })
                }
            />

            {error && <p className="text-sm text-red-400">{error}</p>}

            <div className="flex justify-end">
                <NextButton
                    label={t('form.buttons.next')}
                    disabled={
                        selectedSourceIds.length === 0 ||
                        maxAlertUnits === undefined ||
                        selectionBlocked
                    }
                    tooltip={overCapMessage}
                    onNext={onNext}
                />
            </div>
        </div>
    );
}
